// Customer profile: one person across the organization. Shared details (name, language, gender, reminders
// opt-out) are the same for every branch; bookings and leads show only the branches the viewer can see.
// Preferences: preferred branch (organization-wide) and preferred stylist per branch.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api, { formatDate, formatDateTime } from '../services/api';
import { BranchTag, useEngagementScope } from '../context/EngagementScope';
import { AppointmentStatus, Loader, Modal, Spinner, contactLabel } from './ui';

const GENDERS = [
  { key: '', label: 'Not set' },
  { key: 'female', label: 'Female' },
  { key: 'male', label: 'Male' },
];

function isUpcomingAppointment(appointment, now) {
  if (appointment.status !== 'booked' || !appointment.starts_at) return false;
  const starts = new Date(appointment.starts_at).getTime();
  return Number.isFinite(starts) && starts > now;
}

function groupUpcomingBookings(rows) {
  const groups = [];
  const byKey = new Map();
  for (const appointment of rows) {
    const key = appointment.booking_id != null && appointment.booking_id !== ''
      ? `booking-${appointment.booking_id}`
      : `line:${appointment.id}`;
    let group = byKey.get(key);
    if (!group) {
      group = { key, lines: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.lines.push(appointment);
  }
  for (const group of groups) {
    group.lines.sort((a, b) => {
      const ta = String(a.starts_at || '');
      const tb = String(b.starts_at || '');
      if (ta !== tb) return ta < tb ? -1 : 1;
      return (Number(a.line_no) || 0) - (Number(b.line_no) || 0);
    });
  }
  groups.sort((a, b) => String(a.lines[0].starts_at || '').localeCompare(String(b.lines[0].starts_at || '')));
  return groups;
}

export default function CustomerProfileModal({ customerId, onClose }) {
  const { branches, scopeType } = useEngagementScope();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [flows, setFlows] = useState([]);
  const [stopping, setStopping] = useState(null);

  // The automations this customer is in now (every branch the viewer may see).
  const loadFlows = () =>
    api
      .listRuns({ customer_id: customerId, status: 'open', limit: 20, franchise_id: undefined })
      .then((page) => setFlows(page.items || []))
      .catch(() => setFlows([]));

  const load = () =>
    api
      .customerProfile(customerId)
      .then((d) => {
        setData(d);
        setForm({ name: d.customer.name || '', language: d.customer.language || '', gender: d.customer.gender || '', opted_out: d.customer.opted_out });
      })
      .catch((e) => {
        toast.error(e.message);
        onClose();
      });

  useEffect(() => {
    load();
    loadFlows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  if (!data || !form) {
    return (
      <Modal title="Customer" onClose={onClose} size="wide">
        <Loader />
      </Modal>
    );
  }
  const c = data.customer;
  const changed =
    form.name !== (c.name || '') || form.language !== (c.language || '') || form.gender !== (c.gender || '') || form.opted_out !== c.opted_out;

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      if (form.name !== (c.name || '')) patch.name = form.name;
      if (form.language !== (c.language || '')) patch.language = form.language;
      if (form.gender !== (c.gender || '')) patch.gender = form.gender;
      if (form.opted_out !== c.opted_out) patch.opted_out = form.opted_out;
      await api.updateCustomer(customerId, patch);
      toast.success('Customer updated');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const prefer = async (franchiseId) => {
    try {
      await api.setCustomerPreferences(customerId, { preferred_franchise_id: Number(franchiseId) });
      toast.success('Preferred branch saved');
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const pauseAutomations = async () => {
    setPausing(true);
    try {
      await api.pauseCustomerAutomations(customerId);
      toast.success('Automated messages paused');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPausing(false);
    }
  };

  const resumeAutomations = async () => {
    setPausing(true);
    try {
      await api.resumeCustomerAutomations(customerId);
      toast.success('Automated messages resumed');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPausing(false);
    }
  };

  const stopFlow = async (run) => {
    setStopping(run.id);
    try {
      await api.stopRun(run.id);
      toast.success(`Stopped: ${run.automation_name}`);
      await loadFlows();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setStopping(null);
    }
  };

  const pausedUntil = c.automation_paused_until ? new Date(c.automation_paused_until).getTime() : NaN;
  const automationPaused = Number.isFinite(pausedUntil) && pausedUntil > Date.now();
  const now = Date.now();
  const upcoming = (data.appointments || []).filter((a) => isUpcomingAppointment(a, now));
  const earlier = (data.appointments || []).filter((a) => !isUpcomingAppointment(a, now));
  const upcomingGroups = groupUpcomingBookings(upcoming);

  return (
    <Modal title={c.name || contactLabel(c.phone, c.instagram_username)} onClose={onClose} size="xwide">
      <div className="de-small de-muted" style={{ marginBottom: 12 }}>
        {contactLabel(c.phone, c.instagram_username)} · customer since {formatDate(c.created_at)}
        {c.opted_out ? ' · opted out of reminders' : ''}
      </div>

      <div className="de-section-label">Shared details (every branch)</div>
      <div className="de-form-grid de-form-grid--2">
        <label className="de-field">
          <span className="de-label">Name</span>
          <input className="de-input de-input--bordered" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        </label>
        <label className="de-field">
          <span className="de-label">Language</span>
          <input className="de-input de-input--bordered" value={form.language} onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))} placeholder="Tamil, English…" />
        </label>
        <label className="de-field">
          <span className="de-label">Gender (for service lists)</span>
          <select className="de-select" value={form.gender} onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))}>
            {GENDERS.map((g) => (
              <option key={g.key} value={g.key}>
                {g.label}
              </option>
            ))}
          </select>
        </label>
        <div style={{ alignSelf: 'end', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
          <label className="de-check">
            <input type="checkbox" checked={form.opted_out} onChange={(e) => setForm((f) => ({ ...f, opted_out: e.target.checked }))} />
            <span>No automated reminders or offers (every branch)</span>
          </label>
          {automationPaused ? (
            <span className="de-small">
              Automated messages paused until {formatDateTime(c.automation_paused_until)}
              {' '}
              <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={pausing} onClick={resumeAutomations}>
                Resume
              </button>
            </span>
          ) : (
            <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={pausing} onClick={pauseAutomations}>
              Pause automated messages
            </button>
          )}
        </div>
      </div>
      <div className="de-form-actions" style={{ marginTop: 12 }}>
        <button type="button" className="de-btn de-btn--primary de-btn--sm" disabled={!changed || saving} onClick={save}>
          {saving ? <Spinner light /> : null}
          Save details
        </button>
      </div>

      <div className="de-section-label de-mt-16">In an automation now</div>
      {flows.length ? (
        <div className="de-table-wrap" style={{ marginBottom: 8 }}>
          <table className="de-table">
            <tbody>
              {flows.map((run) => (
                <tr key={run.id}>
                  <td className="de-td-strong">{run.automation_name}</td>
                  <td className="de-small de-muted">
                    {run.status === 'paused' ? 'Paused' : 'Next step'}
                    {run.next_run_at && run.status !== 'paused' ? ` ${formatDateTime(run.next_run_at)}` : ''}
                  </td>
                  <td className="de-td-right">
                    <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={stopping === run.id} onClick={() => stopFlow(run)}>
                      {stopping === run.id ? <Spinner /> : null}
                      Stop
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="de-small de-muted">Not in any automation right now.</div>
      )}

      <div className="de-section-label de-mt-16">Branches</div>
      {data.branches.length === 0 ? (
        <div className="de-small de-muted">No branch yet (the customer has not chosen one).</div>
      ) : (
        <div className="de-table-shell">
          <div className="de-table-wrap">
            <table className="de-table">
              <thead>
                <tr>
                  <th>Branch</th>
                  <th>Bookings</th>
                  <th>Visits</th>
                  <th>Last booked</th>
                  <th>Preferred stylist</th>
                  <th>Preferred</th>
                </tr>
              </thead>
              <tbody>
                {data.branches.map((b) => (
                  <tr key={b.franchise_id}>
                    <td className="de-td-strong">{b.branch_name}</td>
                    <td>{b.booking_count}</td>
                    <td>{b.visit_count}</td>
                    <td className="de-td-muted">{b.last_booked_at ? formatDate(b.last_booked_at) : '—'}</td>
                    <td>
                      {b.preferred_staff_name || '—'}
                      {b.preferred_staff_source ? <span className="de-small de-muted"> ({b.preferred_staff_source})</span> : null}
                    </td>
                    <td>
                      {b.is_preferred ? (
                        <span className="de-pill de-pill--gold de-pill--sm">Preferred{b.preferred_source ? ` · ${b.preferred_source}` : ''}</span>
                      ) : (
                        <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => prefer(b.franchise_id)}>
                          Make preferred
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {scopeType !== 'franchise' && branches.length > 1 && data.preferred_franchise_id == null ? (
        <div className="de-small de-muted de-mt-8">No preferred branch yet: it is worked out from their bookings, or set it above.</div>
      ) : null}

      <div className="de-section-label de-mt-16">Upcoming bookings</div>
      {upcomingGroups.length === 0 ? (
        <div className="de-small de-muted">{data.appointments.length === 0 ? 'No appointments.' : 'No upcoming bookings.'}</div>
      ) : (
        upcomingGroups.map((group) => (
          <div key={group.key} className="de-small" style={{ marginTop: 10 }}>
            <div className="de-td-strong">Booking · {formatDateTime(group.lines[0].starts_at)}</div>
            {group.lines.map((a) => (
              <div key={a.id} className="de-small" style={{ marginTop: 4, paddingLeft: 14 }}>
                {a.service_name || 'Service'}
                {' · '}
                {a.staff_name || '—'}
                {' · '}
                {formatDateTime(a.starts_at)}
                {' · '}
                <AppointmentStatus status={a.status} />
              </div>
            ))}
          </div>
        ))
      )}

      {earlier.length ? (
        <>
          <div className="de-section-label de-mt-16">Appointments {scopeType === 'franchise' ? 'at your branch' : ''}</div>
          <div className="de-table-shell">
            <div className="de-table-wrap" style={{ maxHeight: '36vh', overflowY: 'auto' }}>
              <table className="de-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Service</th>
                    <th>Branch</th>
                    <th>Stylist</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {earlier.map((a) => (
                    <tr key={a.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(a.starts_at)}</td>
                      <td>
                        {a.service_name}
                        {a.service_price ? <span className="de-small de-muted"> · {a.service_price}</span> : null}
                      </td>
                      <td>
                        <BranchTag franchiseId={a.franchise_id} name={a.branch_name} />
                      </td>
                      <td>{a.staff_name || '—'}</td>
                      <td>
                        <AppointmentStatus status={a.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </Modal>
  );
}
